import { Avatar, Button, Flex, Form, Input, Typography } from 'antd'
import { useState } from 'react'
import { Api } from '~/core/trpc'
import { ToolboxClient } from '~/plugins/toolbox/client'

type FormValues = { name: string; password: string }

type Props = {
  email: string
  token?: string
  onError: (key: string) => void
  onSuccess?: () => void
}

export const StepSignup: React.FC<Props> = ({
  email,
  token,
  onError,
  onSuccess,
}) => {
  const [form] = Form.useForm<FormValues>()
  const [isFormValid, setFormValid] = useState(false)
  const [isLoading, setLoading] = useState(false)

  const autofocus = ToolboxClient.useAutofocus()

  const register = Api.authentication.register.useMutation()
  const login = Api.authentication.login.useMutation()

  const handleSubmit = async ({ name, password }: FormValues) => {
    if (isLoading) {
      return
    }

    setLoading(true)

    try {
      await register.mutateAsync({
        email,
        name,
        password,
        tokenInvitation: token,
      })

      const { error } = await login.mutateAsync({ email, password })

      if (error) {
        onError(error)
        setLoading(false)
      } else {
        if (onSuccess) {
          onSuccess()
        } else {
          window.location.href = '/'
        }
      }
    } catch (error) {
      console.error('Unknown Error', error)
      onError('offline')
      setLoading(false)
    }
  }

  const handleChange = async (values: FormValues) => {
    const { password } = values
    setFormValid(typeof password === 'string' && password.trim() !== '')
  }

  return (
    <>
      <Flex align="center" gap={8} className="mb-4">
        <Avatar size="small">{email[0].toUpperCase()}</Avatar>
        <Typography.Text strong>{email}</Typography.Text>
      </Flex>

      <Flex vertical gap={16}>
        <Form
          form={form}
          onFinish={handleSubmit}
          onValuesChange={handleChange}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="name"
            rules={[{ required: true, message: 'Name is required' }]}
            label="Name"
          >
            <Input ref={autofocus.ref} placeholder="Your name" />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Password is required' }]}
          >
            <Input.Password
              type="password"
              placeholder="Your password..."
              autoComplete="current-password"
              minLength={6}
            />
          </Form.Item>

          <Button
            htmlType="submit"
            block
            type="primary"
            disabled={!isFormValid}
            loading={isLoading}
          >
            Register
          </Button>
        </Form>
      </Flex>
    </>
  )
}
